import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { countries } from '../../../data/countries'; // Import countries data
import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';
// ✅ Import the addSupplier service
import { addSupplier } from '../services/supplierService';
import { useTranslation } from "react-i18next";

const AddSupplierPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    taxNumber: '',
    bankAccount: '',
    paymentTerms: '',
    notes: '',          // ✅ Keep notes but will limit to 500 chars
    isActive: true
    // ✅ Removed website: ''
  });

  // Form validation errors
  const [errors, setErrors] = useState({});

  // Add state for user interaction tracking
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
    }

    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
    
    // ✅ Enforce 500 character limit for notes field
    if (name === 'notes' && value.length > 500) {
      return; // Don't update if exceeding 500 characters
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name] && touchedFields[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Add handleFieldBlur function
  const handleFieldBlur = (event) => {
    const { name, value } = event.target;
    
    if (hasUserInteracted || hasAttemptedSubmit) {
      validateSingleField(name, value);
    }
  };

  // ✅ Update validateSingleField for new requirements
  const validateSingleField = (fieldName, value) => {
    if (!hasUserInteracted && !hasAttemptedSubmit) return;

    let error = '';
    
    switch (fieldName) {
      case 'name':
        if (!value.trim()) {
          error = t('supplier_name_required');
        } else if (value.length > 100) {
          error = t('supplier_name_max_length');
        }
        break;
      case 'contactPerson':
        // ✅ Now optional, only validate length if provided
        if (value && value.length > 100) {
          error = t('contact_person_max_length');
        }
        break;
      case 'email':
        // ✅ Now optional, only validate format if provided
        if (value && !/\S+@\S+\.\S+/.test(value)) {
          error = t('email_invalid');
        }
        break;
      case 'taxNumber':
        if (value && value.length > 50) {
          error = t('tax_number_max_length');
        }
        break;
      case 'address':
        if (value && value.length > 100) {
          error = t('address_max_length');
        }
        break;
      case 'postalCode':
        if (value && value.length > 20) {
          error = t('postal_code_max_length');
        }
        break;
      case 'city':
        if (value && value.length > 50) {
          error = t('city_max_length');
        }
        break;
      case 'country':
        if (value && value.length > 50) {
          error = t('country_max_length');
        }
        break;
      case 'phone':
        if (value && value.length > 20) {
          error = t('phone_max_length');
        }
        break;
      case 'notes':
        if (value && value.length > 500) {
          error = t('notes_max_length');
        }
        break;
    }

    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  // Add shouldShowError function
  const shouldShowError = (fieldName) => {
    return (hasUserInteracted && touchedFields[fieldName]) || hasAttemptedSubmit;
  };

  // ✅ Update validation to match new requirements
  const validateForm = () => {
    const newErrors = {};

    // ✅ Only supplier name is required
    if (!formData.name.trim()) {
      newErrors.name = t('supplier_name_required');
    } else if (formData.name.length > 100) {
      newErrors.name = t('supplier_name_max_length');
    }

    // ✅ Contact person is now OPTIONAL
    if (formData.contactPerson && formData.contactPerson.length > 100) {
      newErrors.contactPerson = t('contact_person_max_length');
    }

    // ✅ Email is now OPTIONAL but must be valid if provided
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('email_invalid');
    }

    // ✅ Optional field validations based on backend model
    if (formData.taxNumber && formData.taxNumber.length > 50) {
      newErrors.taxNumber = t('tax_number_max_length');
    }

    if (formData.address && formData.address.length > 100) {
      newErrors.address = t('address_max_length');
    }

    if (formData.postalCode && formData.postalCode.length > 20) {
      newErrors.postalCode = t('postal_code_max_length');
    }

    if (formData.city && formData.city.length > 50) {
      newErrors.city = t('city_max_length');
    }

    if (formData.country && formData.country.length > 50) {
      newErrors.country = t('country_max_length');
    }

    if (formData.phone && formData.phone.length > 20) {
      newErrors.phone = t('phone_max_length');
    }

    if (formData.notes && formData.notes.length > 500) {
      newErrors.notes = t('notes_max_length');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Handle form submission using supplier service
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    setHasAttemptedSubmit(true);
    
    if (!validateForm()) {
      toast.error(!formData.name.trim() ? t('please_fill_required_field') : t('please_fix_form_errors'));
      return;
    }

    setLoading(true);

    try {
      // ✅ Map form data to backend model with optional fields
      const supplierData = {
        Name: formData.name.trim(),
        Contactperson: formData.contactPerson.trim() || null, // ✅ Now optional
        Email: formData.email.trim() || null, // ✅ Now optional
        BTWNumber: formData.taxNumber.trim() || null,
        Street: formData.address.trim() || null,
        StreetNumber: null,
        PostCode: formData.postalCode.trim() || null,
        City: formData.city.trim() || null,
        Country: formData.country.trim() || null,
        MobileNumber: formData.phone.trim() || null,
        Notes: formData.notes.trim() || null
      };


      // ✅ Call addSupplier service method
      const response = await addSupplier(supplierData);
      
      toast.success('Supplier added successfully!');
      
      // ✅ Navigate back to suppliers list
      navigate('/suppliers', {
        state: { 
          message: 'Supplier added successfully',
          supplierId: response.Id || response.id
        }
      });

    } catch (error) {

      
      // ✅ Handle different error responses
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.errors) {
        // Handle validation errors from backend
        setErrors(error.response.data.errors);
        toast.error(t('please_fix_form_errors'));
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error(t('supplier_add_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/suppliers');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 1, sm: 2 }, mb: 4, px: { xs: 1, sm: 2, md: 3 }, width: '100%', minWidth: 0 }}>
      {/* ✅ Updated Header - smaller, more elegant */}
      <Box sx={{ mb: 2 }}>
        <Typography 
          variant="h5" 
          component="h1" 
          sx={{ 
            fontWeight: 600, 
            color: 'primary.main',
            textAlign: 'left', 
              mb: 0.5,
              fontSize: { xs: '1.75rem', sm: '2.125rem' }
          }}
        >
          {t("add_supplier")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("manage_suppliers_desc")}
        </Typography>
      </Box>

      <Paper 
        elevation={2} 
        sx={{ 
          p: { xs: 1.5, sm: 3 }, // ✅ Reduced padding from 4 to 3
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <form onSubmit={handleSubmit} noValidate> {/* ✅ ADD noValidate HERE */}
          <Grid container spacing={2.5}> {/* ✅ Reduced spacing from 3 to 2.5 */}
            
            {/* ✅ Basic Information Section - Improved styling */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5, // ✅ Reduced margin
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem' // ✅ Slightly smaller section headers
                  }}
                >
                  {t("basic_information")}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("supplier_name") + " *"} // ✅ Only this is required
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.name && shouldShowError('name'))}
                helperText={shouldShowError('name') ? errors.name : 'Max 100 characters'}
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("contact_person")} // ✅ Removed asterisk - now optional
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.contactPerson && shouldShowError('contactPerson'))}
                helperText={shouldShowError('contactPerson') ? errors.contactPerson : 'Max 100 characters (optional)'}
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("email_address")} // ✅ Removed asterisk - now optional
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.email && shouldShowError('email'))}
                helperText={shouldShowError('email') ? errors.email : 'Valid email format (optional)'}
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("phone_number")}
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.phone && shouldShowError('phone'))}
                helperText={shouldShowError('phone') ? errors.phone : ' '}
                // required  // ✅ REMOVE THIS
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("vat_number")}
                name="taxNumber"
                value={formData.taxNumber}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.taxNumber && shouldShowError('taxNumber'))}
                helperText={shouldShowError('taxNumber') ? errors.taxNumber : ' '}
                disabled={loading}
                placeholder="BE0123456789"
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 1.5,
                    '&.Mui-error': {
                      '& fieldset': {
                        borderColor: '#d32f2f',
                        borderWidth: '2px'
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    '&.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    marginLeft: 0.5,
                    fontSize: '0.75rem',
                    '&.Mui-error': {
                      color: '#d32f2f'
                    }
                  }
                }}
              />
            </Grid>

            {/* Removed Company Registration Number field as per requirements */}

            {/* ✅ Address Information Section - Improved styling */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5, // ✅ Reduced margin
                mt: 2, // ✅ Reduced top margin
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  {t("address_information")}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("address")}
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.address && shouldShowError('address'))}
                helperText={shouldShowError('address') ? errors.address : ' '}
                // required  // ✅ REMOVE THIS
                disabled={loading}
                multiline
                rows={2}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                  '& .MuiFormHelperText-root': {
                    display: 'flex',
                    justifyContent: 'space-between'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("city")}
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.city && shouldShowError('city'))}
                helperText={shouldShowError('city') ? errors.city : ' '}
                // required  // ✅ REMOVE THIS
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                  '& .MuiFormHelperText-root': {
                    display: 'flex',
                    justifyContent: 'space-between'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("postal_code")}
                name="postalCode"
                value={formData.postalCode}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.postalCode && shouldShowError('postalCode'))}
                helperText={shouldShowError('postalCode') ? errors.postalCode : ' '}
                // required  // ✅ REMOVE THIS
                disabled={loading}
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                  '& .MuiFormHelperText-root': {
                    display: 'flex',
                    justifyContent: 'space-between'
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl 
                fullWidth 
                error={!!errors.country} 
                disabled={loading}
                size="medium"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              >
                <InputLabel>{t("country")}</InputLabel>
                <Select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  label={t("country") + " *"} // Indicate required field
                  // required  // ✅ REMOVE THIS
                >
                  {countries.map((country) => (
                    <MenuItem key={country.code} value={country.name}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.country && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.country}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* ✅ Banking Information Section - Improved styling */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                mt: 2,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  {t("banking_information")}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label={t("bank_account")}
                name="bankAccount"
                value={formData.bankAccount}
                onChange={handleInputChange}
                error={!!errors.bankAccount}
                helperText={errors.bankAccount || t("iban_placeholder")}
                disabled={loading}
                placeholder="BE68 5390 0754 7034"
                inputProps={{ style: { textTransform: 'uppercase' } }}
                size="medium"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("bic")}
                name="bic"
                value={formData.bic}
                onChange={handleInputChange}
                error={!!errors.bic}
                helperText={errors.bic || t("bic_placeholder")}
                disabled={loading}
                placeholder="KREDBEBB"
                inputProps={{ style: { textTransform: 'uppercase' } }}
                size="medium"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
              />
            </Grid>

            {/* ✅ Additional Information Section - Improved styling */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                mt: 2,
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: '1.1rem'
                  }}
                >
                  {t("additional_information")}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("notes")}
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                error={!!(errors.notes && shouldShowError('notes'))}
                helperText={
                  shouldShowError('notes') ? errors.notes : 
                  `${formData.notes.length}/500 ${t("characters")}` // ✅ Show character count
                }
                disabled={loading}
                multiline
                rows={4}
                size="medium"
                inputProps={{ 
                  maxLength: 500  // ✅ HTML maxLength as backup
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                  '& .MuiFormHelperText-root': {
                    display: 'flex',
                    justifyContent: 'space-between'
                  }
                }}
                placeholder={t("notes_placeholder")}
              />
            </Grid>

            {/* ✅ Action Buttons - Improved styling */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
                flexDirection: { xs: 'column-reverse', sm: 'row' },
                mt: 3, // ✅ Reduced from 4 to 3
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancel}
                  disabled={loading}
                  sx={{ 
                    minWidth: { xs: 0, sm: 120 },
                    width: { xs: '100%', sm: 'auto' },
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  sx={{ 
                    minWidth: { xs: 0, sm: 120 },
                    width: { xs: '100%', sm: 'auto' },
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: 2
                  }}
                >
                  {loading ? t("creating") : t("create_supplier")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default AddSupplierPage;